"use client";

import type { Variants } from "framer-motion";
import { motion, useAnimation, useReducedMotion } from "framer-motion";
import { forwardRef, useCallback, useImperativeHandle, useRef } from "react";

export interface LinkIconHandle {
  startAnimation: () => void;
  stopAnimation: () => void;
}

interface LinkIconProps {
  size?: number;
  duration?: number;
  isAnimated?: boolean;
  isHovered?: boolean;
  className?: string;
}

const LinkIcon = forwardRef<LinkIconHandle, LinkIconProps>(
  (
    {
      className,
      size = 24,
      duration = 1,
      isAnimated = true,
      isHovered = false,
    },
    ref,
  ) => {
    const leftPartControls = useAnimation();
    const rightPartControls = useAnimation();
    const reduced = useReducedMotion();
    const isControlled = useRef(false);
    const prevHovered = useRef(isHovered);

    useImperativeHandle(ref, () => {
      isControlled.current = true;
      return {
        startAnimation: () => {
          if (reduced) {
            leftPartControls.start("normal");
            rightPartControls.start("normal");
          } else {
            leftPartControls.start("animate");
            rightPartControls.start("animate");
          }
        },
        stopAnimation: () => {
          leftPartControls.start("normal");
          rightPartControls.start("normal");
        },
      };
    });

    // React to isHovered prop changes
    if (isHovered !== prevHovered.current) {
      prevHovered.current = isHovered;
      if (isHovered && isAnimated && !reduced) {
        leftPartControls.start("animate");
        rightPartControls.start("animate");
      } else if (!isHovered) {
        leftPartControls.start("normal");
        rightPartControls.start("normal");
      }
    }

    const handleEnter = useCallback(() => {
      if (!isAnimated || reduced) return;
      if (!isControlled.current) {
        leftPartControls.start("animate");
        rightPartControls.start("animate");
      }
    }, [leftPartControls, rightPartControls, reduced, isAnimated]);

    const handleLeave = useCallback(() => {
      if (!isControlled.current) {
        leftPartControls.start("normal");
        rightPartControls.start("normal");
      }
    }, [leftPartControls, rightPartControls]);

    const linkVariantsLeft: Variants = {
      normal: { scale: 1, rotate: 0, x: 0 },
      animate: {
        x: [0, -2, 0],
        rotate: [0, -3, 0],
        transition: {
          duration: 0.9 * duration,
          ease: "easeInOut",
        },
      },
    };

    const linkVariantsRight: Variants = {
      normal: { scale: 1, rotate: 0, x: 0 },
      animate: {
        x: [0, 2, 0],
        rotate: [0, 3, 0],
        transition: {
          duration: 0.9 * duration,
          ease: "easeInOut",
        },
      },
    };

    return (
      <motion.div
        className={`inline-flex items-center justify-center ${className || ""}`}
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeave}
        style={{ flexShrink: 0 }}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width={size}
          height={size}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <motion.path
            d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"
            variants={linkVariantsRight}
            initial="normal"
            animate={rightPartControls}
          />
          <motion.path
            d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"
            variants={linkVariantsLeft}
            initial="normal"
            animate={leftPartControls}
          />
        </svg>
      </motion.div>
    );
  },
);

LinkIcon.displayName = "LinkIcon";
export { LinkIcon };
